import {
  useApplyImpersonation,
  useCurrentImpersonation,
  useImpersonationDraft,
} from '@gripp/shared-logic';
import { ReactNode, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';
import { Button as CropCircleButton } from '../crop-circle/Button';
import { CardButton } from '../crop-circle/CardButton/CardButton';
import { spacing } from '../crop-circle/tokens';

type ImpersonationViewProps = {
  onPickWorkspace: () => void;
  onPickUser: () => void;
  onAfterMutation?: () => void;
  title?: ReactNode;
};

export const ImpersonationView = ({
  onPickWorkspace,
  onPickUser,
  onAfterMutation,
  title,
}: ImpersonationViewProps) => {
  const { t } = useTranslation();
  const current = useCurrentImpersonation();
  const { draft, setDraft, clearDraft } = useImpersonationDraft();
  const { cancel } = useApplyImpersonation();
  const suppressRefillRef = useRef(false);

  useEffect(() => {
    if (suppressRefillRef.current) {
      if (!current.isImpersonating) {
        suppressRefillRef.current = false;
      }
      return;
    }
    const draftHasSelection =
      draft.workspaceId !== undefined || draft.userId !== undefined;
    if (!draftHasSelection && current.isImpersonating) {
      setDraft({
        workspaceId: current.workspaceId,
        workspaceName: current.workspaceName,
        workspaceType: current.workspaceType,
        userId: current.userId,
        userName: current.userName,
      });
    }
  }, [
    current.isImpersonating,
    current.workspaceId,
    current.userId,
    current.workspaceName,
    current.userName,
    current.workspaceType,
    draft.workspaceId,
    draft.userId,
    setDraft,
  ]);

  const isDirty =
    draft.workspaceId !== current.workspaceId ||
    draft.userId !== current.userId;

  // Applying an impersonation now happens directly from the user picklist
  // (any selection there auto-applies), so the only button left here is
  // Unimpersonate — shown while the active impersonation matches the draft.
  const showCancel = current.isImpersonating && !isDirty;

  const onUnimpersonate = async () => {
    await cancel();
    suppressRefillRef.current = true;
    clearDraft();
    if (Platform.OS === 'web') {
      window.location.reload();
    } else {
      onAfterMutation?.();
    }
  };

  const workspaceLabel =
    draft.workspaceId === ''
      ? t('impersonation.unassigned')
      : draft.workspaceName ?? t('impersonation.row.workspaceEmpty');
  const userLabel = draft.userName ?? t('impersonation.row.userEmpty');

  const titleNode =
    typeof title === 'string' ? (
      <Text variant="bodyLarge">{title}</Text>
    ) : (
      title
    );

  return (
    <View style={styles.container}>
      {titleNode}
      <CardButton
        showAvatars={false}
        leftIcon={'Profile'}
        rightIcon={'Chevron Right'}
        text={t('profile.admin.workspace')}
        text2={workspaceLabel}
        showText2={true}
        onPress={onPickWorkspace}
        bordered={false}
      />
      <CardButton
        showAvatars={false}
        leftIcon={'People'}
        rightIcon={'Chevron Right'}
        text={t('profile.admin.workspaceUsers')}
        text2={userLabel}
        showText2={true}
        onPress={onPickUser}
        bordered={false}
      />
      {showCancel && (
        <CropCircleButton
          variant="rectangular"
          state="outline"
          size="large"
          onPress={onUnimpersonate}
          style={styles.button}
        >
          {t('profile.edit.impersonate.unimpersonate')}
        </CropCircleButton>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: spacing.md,
  },
  button: {
    marginTop: spacing.xs,
  },
});
